import { n, DEFAULT_GIRTHS } from './calcUtils';
import JSZip from 'jszip';

// ─── Painting Speed options (exported for use in forms) ─────────────────────
export const SPEED_OPTIONS = [
  { label: 'Very Fast', value: 25 },
  { label: 'Fast', value: 20 },
  { label: 'Normal', value: 16 },
  { label: 'Slow', value: 12 },
  { label: 'Very Slow', value: 6 },
];

// ─── Template row map ─────────────────────────────────────────────────────────
const INT_HEADERS = [
  46, 107, 162, 217, 272, 327, 382, 437, 492, 547,
  602, 657, 712, 767, 822, 877, 932, 987, 1042, 1097
];
const EXT_HEADERS = [
  1156, 1218, 1274, 1330, 1386, 1442, 1498, 1554, 1610, 1666,
  1722, 1778, 1834, 1890, 1946, 2002, 2058, 2114, 2170, 2226
];

const INT_OTHER_OFFSETS = [8, 9, 10, 11, 12, 13, 14, 15];
const INT_NAMED_OFFSETS = [2, 3, 4, 5, 6, 7];
const INT_GAP_OFFSET = 16;

const EXT_SURFACE_OFFSETS = {
  'Gutter': 2, 'Fascia': 3, 'Eaves': 4, 'Soffit': 5,
  'Walls': 6, 'Walls - Flat': 6, 'Walls Rough': 6,
  'Windows': 7, 'Windows (LM)': 7, 'Windows (M2)': 7,
  'Floors': 8, 'Floor': 8,
  'Slab Edge': 9, 'Slab Edges': 9,
  'Downpipes': 10, 'Doors': 11,
  'Balustrade / Handrails': 12, 'Balustrades/Handrails': 12, 'Posts': 13,
};
const EXT_OTHER_OFFSETS = [14, 15, 16];
const EXT_NAMED_OFFSETS = [2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13];
const EXT_GAP_OFFSET = 17;
const EXT_WASH_OFFSET = 19;

// Exclusion & notes offsets
const INT_EXCL_OFFSETS_A1 = [46, 47, 48, 49];
const INT_NOTE_OFFSET_A1 = 51;
const INT_EXCL_OFFSET = 46;
const INT_NOTE_OFFSET = 47;
const EXT_EXCL_OFFSETS_A1 = [47, 48, 49, 50];
const EXT_NOTE_OFFSET_A1 = 52;
const EXT_EXCL_OFFSET = 47;
const EXT_NOTE_OFFSET = 48;

// Abseil % row offsets (col H = percentage that is being abseiled)
const INT_ABSEIL_OFFSET = 43;
const EXT_ABSEIL_OFFSET = 44;

// Total row offsets (SUMPRODUCT)
const EXT_TOTAL_A1 = 57;
const EXT_TOTAL = 51;
// Downtime row offsets
const INT_DOWNTIME_OFFSET = 20;
const EXT_DOWNTIME_OFFSET = 21;

// Preliminary items row offsets (8 slots per area)
const INT_PRELIM_OFFSETS = [29, 30, 31, 32, 33, 34, 35, 36];
const EXT_PRELIM_OFFSETS = [30, 31, 32, 33, 34, 35, 36, 37];

// Summary sheet prelim rows (E91–E105, 15 slots)
const SUMMARY_PRELIM_START = 91;
const SUMMARY_PRELIM_END = 105;

// Module-scoped flag for exclude-sound export mode (Mode 2). Set per export run in
// exportProjectToExcel. When true, surfaces rated Good (green) export with 0 coats and
// 0 prep so the template formulas zero their hours/cost/paint. Not re-entrant (one
// export at a time), so a module flag is safe and avoids threading a param through 6
// writeSurfSettings call sites.
let _excludeSoundMode = false;

// ─── Cell helpers ─────────────────────────────────────────────────────────────

function toAddr(row, col) {
  let s = '', c = col;
  while (c > 0) { s = String.fromCharCode(((c - 1) % 26) + 65) + s; c = Math.floor((c - 1) / 26); }
  return s + row;
}

function escXml(str) {
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

function cellRe(addr) {
  const a = addr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  // Single capture group for attributes. [^>/]* prevents capturing '/' of self-closing tags.
  // Handles: self-closing <c r="A1" s="5"/>, content <c r="A1" t="s"><v>3</v></c>, empty <c r="A1"></c>
  return new RegExp(
    '<c r="' + a + '"([^>/]*)(?:/>|>[\\s\\S]*?</c>)',
    'g'
  );
}

function styleAttr(attrStr) {
  const m = (attrStr || '').match(/\bs="(\d+)"/);
  return m ? ` s="${m[1]}"` : '';
}

// ─── Strip shared formulas (ONLY in columns we patch) ──────────────────────
// The template uses Excel shared formulas (<f t="shared" si="N" ref="...">).
// When we patch a source cell, the dependent cells (<f t="shared" si="N"/>) become
// orphaned, causing Excel's "Shared formula removed" repair warning on open.
// Fix: only strip shared formula groups whose ref starts in a column we write to.
// Column I (wage rates), F-H, J-L, N-Z+ keep their shared formulas intact.
const PATCHED_COLS = new Set(['A','B','C','D','E','M']);

function stripSharedFormulas(xml) {
  // 1. Parse source cells to find which si groups belong to patched columns.
  //    Source format: <f t="shared" ref="D48:D61" si="5">formula</f>
  //    The ref's starting column tells us which column owns this group.
  const patchedSi = new Set();
  const srcRe = /ref="([A-Z]+)\d+(?::[A-Z]+\d+)?"[^>]*si="(\d+)"|si="(\d+)"[^>]*ref="([A-Z]+)\d+/g;
  let m;
  while ((m = srcRe.exec(xml)) !== null) {
    const col = m[1] || m[4];
    const si  = m[2] || m[3];
    if (col && si && PATCHED_COLS.has(col)) patchedSi.add(si);
  }

  // 2. Source cells in patched columns: <f t="shared" ref="..." si="N">formula</f> → <f>formula</f>
  xml = xml.replace(/<f\s([^>]*t="shared"[^>]*ref="[^"]*"[^>]*)>([\s\S]*?)<\/f>/g, (match, attrs, formula) => {
    const siMatch = attrs.match(/si="(\d+)"/);
    if (siMatch && patchedSi.has(siMatch[1])) return '<f>' + formula + '</f>';
    return match;
  });

  // 3. Dependent cells of patched groups: <f t="shared" si="N"/> → remove
  xml = xml.replace(/<f\s([^>]*t="shared"[^>]*)\/>/g, (match, attrs) => {
    const siMatch = attrs.match(/si="(\d+)"/);
    if (siMatch && patchedSi.has(siMatch[1])) return '';
    return match;
  });

  return xml;
}

// ─── Dynamic Row Insertion Engine ─────────────────────────────────────────────
// When an area has more overflow surfaces than available slots, insert extra rows
// into the XML so nothing is silently dropped.

// --- Pre-scan: count extra rows needed per area ---

function countIntExtras(area) {
  const groups = area.repeatGroups || [];
  const ungrouped = (area.surfaceEntries || []).filter(e => !e.repeatGroupId);
  const grouped = (area.surfaceEntries || []).filter(e => e.repeatGroupId);

  const bySubstrate = {};
  ungrouped.forEach(e => {
    const k = e.substrate || '';
    if (!bySubstrate[k]) bySubstrate[k] = [];
    bySubstrate[k].push(e);
  });

  const usedOffsets = new Set();
  const named = { 'Ceilings': 2, 'Walls': 3, 'Floors': 4, 'Doors': 6, 'Skirting': 7 };
  const namedSet = new Set([...Object.keys(named), 'Windows']);

  // Check which named offsets are used. Mirror writeIntArea EXACTLY: an offset is only
  // consumed when the surface actually produces a formula (buildFormula != null), so a
  // measured-but-dimensionless surface doesn't make count and writer disagree.
  const allWin = bySubstrate['Windows'] || [];
  if (allWin.length) {
    const areaWin = allWin.filter(e => (e.windowMeasureType || 'area') !== 'frame');
    const frameWin = allWin.filter(e => (e.windowMeasureType || 'area') === 'frame');
    const winParts = [];
    if (areaWin.length) { const f = buildFormula('Windows (M2)', areaWin.map(e => ({ ...e, winWidth: e.frameWidth, winHeight: e.frameLength }))); if (f) winParts.push(f); }
    if (frameWin.length) { const f = buildFormula('Windows (LM)', frameWin); if (f) winParts.push(f); }
    if (winParts.length) usedOffsets.add(5);
  }
  Object.entries(named).forEach(([surf, offset]) => {
    const entries = bySubstrate[surf] || [];
    if (entries.length && buildFormula(surf, entries)) usedOffsets.add(offset);
  });
  // Door frames use offset 9
  const doorFrameEntries = (bySubstrate['Doors'] || []).filter(e => e.includeFrame);
  if (doorFrameEntries.length && buildFormula('Door frames', doorFrameEntries)) usedOffsets.add(9);

  // Count overflow items (only surfaces that actually produce a formula)
  let overflowCount = 0;
  Object.entries(bySubstrate).forEach(([surf, entries]) => {
    if (namedSet.has(surf) || !surf) return;
    if (buildFormula(surf, entries)) overflowCount++;
  });
  groups.forEach(g => {
    const groupEntries = grouped.filter(e => e.repeatGroupId === g.id);
    const grpBySubstrate = {};
    groupEntries.forEach(e => { const k = e.substrate || ''; if (!k) return; (grpBySubstrate[k] = grpBySubstrate[k] || []).push({ ...e, _repeatCount: g.count || 1 }); });
    Object.entries(grpBySubstrate).forEach(([sub, entries]) => {
      if (buildFormula(sub, entries)) overflowCount++;
    });
  });

  const avail = [...INT_OTHER_OFFSETS, ...INT_NAMED_OFFSETS.filter(o => !usedOffsets.has(o)), INT_GAP_OFFSET].filter(o => !usedOffsets.has(o));
  return Math.max(0, overflowCount - avail.length);
}

function countExtExtras(area) {
  const groups = area.repeatGroups || [];
  const ungrouped = (area.surfaceEntries || []).filter(e => !e.repeatGroupId);
  const grouped = (area.surfaceEntries || []).filter(e => e.repeatGroupId);

  const usedOffsets = new Set();
  const overflow = [];

  // Replicate writeExtArea EXACTLY: only surfaces that produce a formula consume a slot.
  const { map: extMap, order: extOrder } = groupExtBySubstrate(ungrouped);
  extOrder.forEach(sub => {
    if (!buildFormula(sub, extMap[sub])) return;
    const offsetKey = Object.keys(EXT_SURFACE_OFFSETS).find(k => k.trim() === sub);
    const offset = offsetKey !== undefined ? EXT_SURFACE_OFFSETS[offsetKey] : undefined;
    if (offset !== undefined && !usedOffsets.has(offset)) {
      usedOffsets.add(offset);
    } else {
      overflow.push(sub);
    }
  });

  // Repeat groups always go to overflow (only those that produce a formula)
  groups.forEach(g => {
    const groupEntries = grouped.filter(e => e.repeatGroupId === g.id);
    const grpBySubstrate = {};
    groupEntries.forEach(e => { const k = e.substrate || ''; if (!k) return; (grpBySubstrate[k] = grpBySubstrate[k] || []).push({ ...e, _repeatCount: g.count || 1 }); });
    Object.entries(grpBySubstrate).forEach(([sub, entries]) => {
      if (buildFormula(sub, entries)) overflow.push(sub);
    });
  });

  const avail = [...EXT_OTHER_OFFSETS, ...EXT_NAMED_OFFSETS.filter(o => !usedOffsets.has(o)), EXT_GAP_OFFSET].filter(o => !usedOffsets.has(o));
  return Math.max(0, overflow.length - avail.length);
}

// --- Row insertion helpers ---

function calcShift(row, insertions) {
  let shift = 0;
  for (const ins of insertions) {
    if (row > ins.afterRow) shift += ins.shiftBy;
  }
  return shift;
}

// Renumber row r attributes and cell r attributes for rows > afterRow.
// ONLY touches <row r=""> and <c r=""> — formula text is NOT modified.
function renumberRows(xml, afterRow, shiftBy) {
  xml = xml.replace(/<row r="(\d+)"/g, (match, rn) => {
    const r = parseInt(rn);
    if (r > afterRow) return `<row r="${r + shiftBy}"`;
    return match;
  });
  xml = xml.replace(/<c r="([A-Z]+)(\d+)"/g, (match, col, rn) => {
    const r = parseInt(rn);
    if (r > afterRow) return `<c r="${col}${r + shiftBy}"`;
    return match;
  });
  return xml;
}

// Clone a source row's XML, retarget to newRow, clear data columns A/B/C but keep styles
function cloneRow(xml, sourceRow, newRow, isArea1) {
  const rowRe = new RegExp(`<row r="${sourceRow}"([^>]*)>(.*?)</row>`, 's');
  const m = xml.match(rowRe);
  if (!m) { console.warn('ExcelExport: cannot find source row', sourceRow); return null; }

  let rowAttrs = m[1];
  const rowContent = m[2];

  // Set row attributes based on area index
  if (isArea1) {
    rowAttrs = rowAttrs.replace(/\s*hidden="1"/g, '').replace(/\s*outlineLevel="\d+"/g, '');
  } else {
    if (!rowAttrs.includes('hidden="1"')) rowAttrs += ' hidden="1"';
    if (!rowAttrs.includes('outlineLevel=')) rowAttrs += ' outlineLevel="1"';
  }

  let cloned = `<row r="${newRow}"${rowAttrs}>${rowContent}</row>`;

  // Retarget all cell references to newRow
  cloned = cloned.replace(/<c r="([A-Z]+)\d+"/g, (_, col) => `<c r="${col}${newRow}"`);

  // Clear columns A, B, C values but keep style — replace with empty cell
  cloned = cloned.replace(/<c r="([ABC])(\d+)"([^>/]*)(?:\/>|>([\s\S]*?)<\/c>)/g, (match, col, rn, attrs) => {
    const s = styleAttr(attrs);
    return `<c r="${col}${rn}"${s}/>`;
  });

  // NOTE: Standalone (non-shared) formulas still reference the sourceRow.
  // They are fixed AFTER shiftAllFormulas by fixClonedRowFormulas().

  return cloned;
}

// Insert N rows after afterRow into the XML
function insertRowsIntoXml(xml, sourceRow, afterRow, count, isArea1) {
  const clonedRows = [];
  for (let i = 1; i <= count; i++) {
    const newRow = afterRow + i;
    const row = cloneRow(xml, sourceRow, newRow, isArea1);
    if (row) clonedRows.push(row);
  }
  if (!clonedRows.length) return xml;

  // Renumber all existing rows after the insertion point
  xml = renumberRows(xml, afterRow, count);

  // Insert cloned rows after the (un-shifted) afterRow.
  // Handle both content rows (<row ...>...</row>) and self-closing rows (<row .../>).
  const insertAfterRe = new RegExp(
    `(<row r="${afterRow}"[^>]*(?:>[\\s\\S]*?</row>|/>))`, 's'
  );
  xml = xml.replace(insertAfterRe, (match) => {
    return match + clonedRows.join('');
  });

  return xml;
}

// --- Global formula shift ---

function shiftFormulaRefs(formula, insertions) {
  // Match EITHER a sheet-qualified ref/range (preserve as-is — cross-sheet refs to
  // Formulas, REF, Cost PA, etc. must NOT be shifted, because those sheets don't
  // have inserted rows) OR a bare cell reference on the current sheet (shift it).
  const refPattern = /(?:'[^']+'|[A-Za-z_][A-Za-z0-9_]*)!\$?[A-Z]+\$?\d+(?::\$?[A-Z]+\$?\d+)?|(\$?)([A-Z]+)(\$?)(\d+)/g;
  return formula.replace(refPattern, (m, d1, col, d2, row) => {
    // Sheet-qualified branch: capture groups are undefined
    if (col === undefined) return m;
    const r = parseInt(row);
    // Don't shift absolute row references to global config rows (above all insertions)
    if (d2 === '$' && r <= 46) return m;
    const shift = calcShift(r, insertions);
    if (shift > 0) return `${d1}${col}${d2}${r + shift}`;
    return m;
  });
}

// Shift all formula text and shared formula ref attributes in the XML.
// Operates on ORIGINAL row numbers (renumberRows only touched <row>/<c> attrs,
// formula text still has original coordinates).
function shiftAllFormulas(xml, insertions) {
  if (!insertions.length) return xml;
  return xml.replace(/<f([^>/]*)>([\s\S]*?)<\/f>/g, (match, attrs, formula) => {
    const shifted = shiftFormulaRefs(formula, insertions);
    let newAttrs = attrs;
    if (attrs.includes('ref="')) {
      // Range refs: ref="A1:B10"
      newAttrs = attrs.replace(/ref="([A-Z]+)(\d+):([A-Z]+)(\d+)"/g, (rm, c1, r1, c2, r2) => {
        const nr1 = parseInt(r1) + calcShift(parseInt(r1), insertions);
        const nr2 = parseInt(r2) + calcShift(parseInt(r2), insertions);
        return `ref="${c1}${nr1}:${c2}${nr2}"`;
      });
      // Single-cell refs: ref="S64" (no colon). Range refs were just rewritten and still
      // contain ':', so this pass won't re-touch them.
      newAttrs = newAttrs.replace(/ref="([A-Z]+)(\d+)"/g, (rm, c1, r1) => {
        const nr1 = parseInt(r1) + calcShift(parseInt(r1), insertions);
        return `ref="${c1}${nr1}"`;
      });
    }
    if (shifted !== formula || newAttrs !== attrs) return `<f${newAttrs}>${shifted}</f>`;
    return match;
  });
}

// After shiftAllFormulas, extend shared formula ref ranges to cover inserted rows.
// This runs AFTER the global shift so all coords are in final space.
// IMPORTANT: Only extend groups whose ref end includes the source row. Groups with
// shorter ranges (e.g. si=55 AA110:AA116) must NOT be extended, otherwise they'd
// overlap with other groups in the same column (e.g. si=58 AA117:AA122).
function extendSharedFormulaRefsForInsertions(xml, areaInsertions) {
  for (const ai of areaInsertions) {
    const shiftedFirst = ai.shiftedHeader + 2;
    const shiftedSource = ai.shiftedHeader + ai.sourceOffset;
    const shiftedGap = ai.shiftedHeader + ai.gapOffset;
    const targetEnd = shiftedGap + ai.extraRows;

    xml = xml.replace(
      /<f\s([^>]*ref="([A-Z]+)(\d+):([A-Z]+)(\d+)"[^>]*)>/g,
      (match, attrs, col1, r1str, col2, r2str) => {
        const r1 = parseInt(r1str);
        const r2 = parseInt(r2str);
        // Only extend refs that:
        // 1. Start within this area's surface zone
        // 2. End at or after the source row (ensuring the source row is in the group)
        // 3. End at or before the gap row (groups past the gap already cover the territory)
        if (r1 >= shiftedFirst && r1 <= shiftedGap &&
            r2 >= shiftedSource && r2 <= shiftedGap) {
          const newAttrs = attrs.replace(
            `ref="${col1}${r1str}:${col2}${r2str}"`,
            `ref="${col1}${r1str}:${col2}${targetEnd}"`
          );
          return `<f ${newAttrs}>`;
        }
        return match;
      }
    );
  }
  return xml;
}

// Fix standalone formulas in cloned rows. After shiftAllFormulas, standalone
// formula text still references the (possibly shifted) source row instead of the
// cloned row's actual position. Replace shiftedSourceRow refs with the target row.
function fixClonedRowFormulas(xml, areaInsertions, insertions, insertedSlots) {
  for (const ai of areaInsertions) {
    const origSourceRow = ai.header + ai.sourceOffset;
    const shiftedSource = origSourceRow + calcShift(origSourceRow, insertions);
    const key = `${ai.type === 'internal' ? 'int' : 'ext'}_${ai.areaIdx}`;
    const slots = insertedSlots[key] || [];

    for (const slotRow of slots) {
      // Find cells in this row and fix standalone formula refs
      const cellRe2 = new RegExp(
        `(<c r="[A-Z]+${slotRow}"[^>]*>)((?:(?!</c>)[\\s\\S])*?<f(?![^>]*t="shared")[^>]*>)([\\s\\S]*?)(</f>)`,
        'g'
      );
      xml = xml.replace(cellRe2, (match, pre, mid, formula, post) => {
        const fixed = formula.replace(
          /(\$?)([A-Z]+)(\$?)(\d+)/g,
          (m2, d1, col2, d2, row) => {
            if (parseInt(row) === shiftedSource) return `${d1}${col2}${d2}${slotRow}`;
            return m2;
          }
        );
        return `${pre}${mid}${fixed}${post}`;
      });
    }
  }
  return xml;
}

// Fix SUMPRODUCT ranges — extend end to cover gap + inserted rows.
// The SUMPRODUCT is in column C of the total row (t="array" format).
// Its row refs are in original coords and DON'T get shifted by shiftAllFormulas
// (they're ≤ afterRow), so we set the end directly.
function fixSumproductRanges(xml, areaInsertions) {
  for (const ai of areaInsertions) {
    // Target SUMPRODUCT end: gap row + N inserted rows (in final coords)
    const newEnd = ai.shiftedHeader + ai.gapOffset + ai.extraRows;
    const firstSurf = ai.shiftedHeader + 2;

    // Search entire XML for SUMPRODUCT formulas that reference this area's surface range.
    // Each area has a unique first surface row, so this match is unambiguous.
    xml = xml.replace(
      /SUMPRODUCT\(([A-Z]+)(\d+):([A-Z]+)(\d+)\*([A-Z]+)(\d+):([A-Z]+)(\d+)\)/g,
      (match, c1, r1, c2, r2, c3, r3, c4) => {
        if (parseInt(r1) === firstSurf && parseInt(r2) < newEnd) {
          return `SUMPRODUCT(${c1}${r1}:${c2}${newEnd}*${c3}${r3}:${c4}${newEnd})`;
        }
        return match;
      }
    );
  }
  return xml;
}

// --- Summary sheet cross-reference update ---

function shiftSummaryRefs(summaryXml, insertions) {
  if (!insertions.length) return summaryXml;
  return summaryXml.replace(
    /'Take Off'!(\$?)([A-Z]+)(\$?)(\d+)/g,
    (match, d1, col, d2, row) => {
      const r = parseInt(row);
      if (r <= 46) return match; // Metadata rows — never shift
      const shift = calcShift(r, insertions);
      if (shift > 0) return `'Take Off'!${d1}${col}${d2}${r + shift}`;
      return match;
    }
  );
}

// --- Structural element updates ---

function shiftMergeCells(xml, insertions) {
  if (!insertions.length) return xml;
  return xml.replace(/<mergeCell ref="([A-Z]+)(\d+):([A-Z]+)(\d+)"/g, (m, c1, r1, c2, r2) => {
    const nr1 = parseInt(r1) + calcShift(parseInt(r1), insertions);
    const nr2 = parseInt(r2) + calcShift(parseInt(r2), insertions);
    return `<mergeCell ref="${c1}${nr1}:${c2}${nr2}"`;
  });
}

function shiftDimension(xml, totalShift) {
  if (!totalShift) return xml;
  return xml.replace(/<dimension ref="([A-Z]+)(\d+):([A-Z]+)(\d+)"/, (m, c1, r1, c2, r2) => {
    const newR2 = parseInt(r2) + totalShift;
    return `<dimension ref="${c1}${r1}:${c2}${newR2}"`;
  });
}

function adjustBlocks(blocks, insertions) {
  return blocks.map(([s, e, sum, idx]) => [
    s + calcShift(s, insertions),
    e + calcShift(e, insertions),
    sum + calcShift(sum, insertions),
    idx
  ]);
}

// --- Master insertion orchestrator ---

function performRowInsertions(xml, summaryXml, project) {
  const intAreas = (project.areas || []).filter(a => a.type === 'internal');
  const extAreas = (project.areas || []).filter(a => a.type === 'external');

  // Phase 1: Pre-scan all areas
  const intExtras = intAreas.map((a, i) => ({ areaIdx: i, extra: countIntExtras(a), type: 'internal' }));
  const extExtras = extAreas.map((a, i) => ({ areaIdx: i, extra: countExtExtras(a), type: 'external' }));

  const needsInsertion = [...intExtras, ...extExtras].some(e => e.extra > 0);
  if (!needsInsertion) {
    return {
      xml, summaryXml, insertions: [],
      adjustedIntHeaders: [...INT_HEADERS],
      adjustedExtHeaders: [...EXT_HEADERS],
      adjustedIntBlocks: [...INT_BLOCKS],
      adjustedExtBlocks: [...EXT_BLOCKS],
      insertedSlots: {},
    };
  }

  // Phase 2: Insert rows bottom-up (highest row numbers first).
  // When processing bottom-up, each area's rows are at their ORIGINAL positions
  // in the current XML because all prior insertions were at higher row numbers
  // and only shifted rows above them. No cumulativeShift is needed.
  const insertions = [];
  const areaInsertions = [];
  let totalShift = 0;

  // Process external areas 20→1 (index 19→0)
  for (let i = extAreas.length - 1; i >= 0; i--) {
    const extra = extExtras[i].extra;
    if (extra <= 0) continue;

    const origHeader = EXT_HEADERS[i];
    // In current XML, header is at origHeader (bottom-up: no prior insertion above)
    const sourceRow = origHeader + 16; // last Other offset for ext
    const insertAfterRow = origHeader + EXT_GAP_OFFSET;

    xml = insertRowsIntoXml(xml, sourceRow, insertAfterRow, extra, i === 0);

    // Record afterRow in ORIGINAL template coordinates — formula text still uses these
    insertions.push({ afterRow: insertAfterRow, shiftBy: extra });
    areaInsertions.push({
      header: origHeader, extraRows: extra,
      type: 'external', areaIdx: i, gapOffset: EXT_GAP_OFFSET, sourceOffset: 16
    });
    totalShift += extra;
  }

  // Process internal areas 20→1 (index 19→0)
  for (let i = intAreas.length - 1; i >= 0; i--) {
    const extra = intExtras[i].extra;
    if (extra <= 0) continue;

    const origHeader = INT_HEADERS[i];
    const sourceRow = origHeader + 15; // last Other offset for int
    const insertAfterRow = origHeader + INT_GAP_OFFSET;

    xml = insertRowsIntoXml(xml, sourceRow, insertAfterRow, extra, i === 0);

    insertions.push({ afterRow: insertAfterRow, shiftBy: extra });
    areaInsertions.push({
      header: origHeader, extraRows: extra,
      type: 'internal', areaIdx: i, gapOffset: INT_GAP_OFFSET, sourceOffset: 15
    });
    totalShift += extra;
  }

  // Sort insertions ascending by afterRow for calcShift
  insertions.sort((a, b) => a.afterRow - b.afterRow);

  // Phase 3: Global formula shift (formula text still has original row numbers)
  xml = shiftAllFormulas(xml, insertions);

  // Compute shifted headers for post-shift fixups
  for (const ai of areaInsertions) {
    ai.shiftedHeader = ai.header + calcShift(ai.header, insertions);
  }

  // Extend shared formula ref ranges to cover inserted rows (post-shift fixup)
  xml = extendSharedFormulaRefsForInsertions(xml, areaInsertions);

  // Fix SUMPRODUCT ranges (end row needs to include gap + inserted rows)
  xml = fixSumproductRanges(xml, areaInsertions);

  // Phase 4: Update Summary sheet
  summaryXml = shiftSummaryRefs(summaryXml, insertions);

  // Phase 5: Structural updates
  xml = shiftMergeCells(xml, insertions);
  xml = shiftDimension(xml, totalShift);

  const adjustedIntHeaders = INT_HEADERS.map(h => h + calcShift(h, insertions));
  const adjustedExtHeaders = EXT_HEADERS.map(h => h + calcShift(h, insertions));
  const adjustedIntBlocks = adjustBlocks(INT_BLOCKS, insertions);
  const adjustedExtBlocks = adjustBlocks(EXT_BLOCKS, insertions);

  // Calculate inserted slot row numbers per area (in final coordinates)
  const insertedSlots = {};
  for (const ai of areaInsertions) {
    const key = `${ai.type === 'internal' ? 'int' : 'ext'}_${ai.areaIdx}`;
    const gapRow = ai.shiftedHeader + ai.gapOffset;
    const slots = [];
    for (let j = 1; j <= ai.extraRows; j++) {
      slots.push(gapRow + j);
    }
    insertedSlots[key] = slots;
  }

  // Fix standalone formulas in cloned rows (post-shift: replace shifted source refs)
  xml = fixClonedRowFormulas(xml, areaInsertions, insertions, insertedSlots);

  // ── Diagnostic logging (TEMPORARY — remove after verifying fix) ──────────
  console.log('[Diagnostic] Insertions performed:', JSON.stringify(insertions));
  console.log('[Diagnostic] Adjusted int headers:', adjustedIntHeaders);
  console.log('[Diagnostic] Adjusted ext headers:', adjustedExtHeaders);
  for (const ai of areaInsertions) {
    const key = `${ai.type === 'internal' ? 'int' : 'ext'}_${ai.areaIdx}`;
    const slots = insertedSlots[key] || [];
    console.log(`[Diagnostic] Area ${ai.type} idx=${ai.areaIdx} ` +
                `header=${ai.header} shiftedHeader=${ai.shiftedHeader} ` +
                `extraRows=${ai.extraRows} insertedSlots=[${slots.join(',')}]`);
  }
  // Verify inserted rows exist in the XML
  const allInsertedRowNumbers = Object.values(insertedSlots).flat();
  for (const rowNum of allInsertedRowNumbers) {
    const rowPattern = new RegExp(`<row r="${rowNum}"`, 'g');
    const matches = (xml.match(rowPattern) || []).length;
    if (matches === 0) {
      console.error(`[Diagnostic] MISSING ROW after insertion: row ${rowNum}`);
    } else if (matches > 1) {
      console.error(`[Diagnostic] DUPLICATE ROW after insertion: row ${rowNum} (${matches} copies)`);
    }
  }
  // ── End diagnostic logging ───────────────────────────────────────────────

  return {
    xml, summaryXml, insertions, adjustedIntHeaders, adjustedExtHeaders,
    adjustedIntBlocks, adjustedExtBlocks, insertedSlots, totalShift,
  };
}

function patchFormula(xml, addr, formula) {
  const re = cellRe(addr);
  let patched = false;
  const escFormula = escXml(formula);
  const out = xml.replace(re, (_, attrs) => {
    patched = true;
    const s = styleAttr(attrs);
    return `<c r="${addr}"${s}><f>${escFormula}</f></c>`;
  });
  if (patched) return out;

  // Fallback: insert into existing row if cell is missing (sparse row)
  const rowNum = addr.match(/\d+/)?.[0];
  if (rowNum) {
    const rowRe = new RegExp(`(<row r="${rowNum}"[^>/]*>)`);
    let inserted = false;
    const withInsert = xml.replace(rowRe, (match) => {
      inserted = true;
      return match + `<c r="${addr}"><f>${escFormula}</f></c>`;
    });
    if (inserted) {
      console.warn(`ExcelExport: formula cell ${addr} inserted into row ${rowNum} (was missing)`);
      return withInsert;
    }
  }

  console.warn('ExcelExport: cell not found:', addr);
  return xml;
}

// Blank a cell's contents while preserving its style, matching the template's
// empty-row form (e.g. <c r="C1160" s="1524"/>). Used to clear measurement cells
// (column C) of surface rows that are not in scope, so residual template example
// data can never leak into the quote.
function clearFormulaCell(xml, addr) {
  const re = cellRe(addr);
  return xml.replace(re, (_, attrs) => {
    const s = styleAttr(attrs);
    return `<c r="${addr}"${s}/>`;
  });
}

function patchString(xml, addr, value) {
  if (value === null || value === undefined || value === '') return xml;
  const re = cellRe(addr);
  let patched = false;
  const strValue = escXml(String(value));
  const out = xml.replace(re, (_, attrs) => {
    patched = true;
    const s = styleAttr(attrs);
    return `<c r="${addr}"${s} t="inlineStr"><is><t>${strValue}</t></is></c>`;
  });
  if (patched) return out;

  // Fallback: insert into existing row if cell is missing (sparse row)
  const rowNum = addr.match(/\d+/)?.[0];
  if (rowNum) {
    const rowRe = new RegExp(`(<row r="${rowNum}"[^>/]*>)`);
    let inserted = false;
    const withInsert = xml.replace(rowRe, (match) => {
      inserted = true;
      return match + `<c r="${addr}" t="inlineStr"><is><t>${strValue}</t></is></c>`;
    });
    if (inserted) {
      console.warn(`ExcelExport: cell ${addr} inserted into row ${rowNum} (was missing)`);
      return withInsert;
    }
  }

  console.warn('ExcelExport: cell not found:', addr);
  return xml;
}

const ACCESS_EQUIPMENT_IDS = new Set([
  'scissor_lift', 'boom_lift', 'scissor_lift_int', 'boom_lift_int'
]);

function patchRedString(xml, addr, value) {
  if (!value) return xml;
  const re = cellRe(addr);
  let patched = false;
  const richText = `<r><rPr><b/><sz val="10"/><color rgb="FFFF0000"/><rFont val="Arial"/></rPr><t>${escXml(String(value))}</t></r>`;
  const out = xml.replace(re, (_, attrs) => {
    patched = true;
    const s = styleAttr(attrs);
    return `<c r="${addr}"${s} t="inlineStr"><is>${richText}</is></c>`;
  });
  if (patched) return out;

  // Fallback: insert into existing row if cell is missing
  const rowNum = addr.match(/\d+/)?.[0];
  if (rowNum) {
    const rowRe = new RegExp(`(<row r="${rowNum}"[^>/]*>)`);
    let inserted = false;
    const withInsert = xml.replace(rowRe, (match) => {
      inserted = true;
      return match + `<c r="${addr}" t="inlineStr"><is>${richText}</is></c>`;
    });
    if (inserted) {
      console.warn(`ExcelExport: red string cell ${addr} inserted into row ${rowNum} (was missing)`);
      return withInsert;
    }
  }

  console.warn('ExcelExport: cell not found:', addr);
  return xml;
}

function patchNumber(xml, addr, value) {
  if (value === null || value === undefined) return xml;
  const re = cellRe(addr);
  let patched = false;
  const out = xml.replace(re, (_, attrs) => {
    patched = true;
    const s = styleAttr(attrs);
    return `<c r="${addr}"${s}><v>${value}</v></c>`;
  });
  if (patched) return out;

  // Fallback: insert into existing row if cell is missing
  const rowNum = addr.match(/\d+/)?.[0];
  if (rowNum) {
    const rowRe = new RegExp(`(<row r="${rowNum}"[^>/]*>)`);
    let inserted = false;
    const withInsert = xml.replace(rowRe, (match) => {
      inserted = true;
      return match + `<c r="${addr}"><v>${value}</v></c>`;
    });
    if (inserted) {
      console.warn(`ExcelExport: number cell ${addr} inserted into row ${rowNum} (was missing)`);
      return withInsert;
    }
  }

  console.warn('ExcelExport: cell not found:', addr);
  return xml;
}

// ─── Formula builders ─────────────────────────────────────────────────────────

// Try to collapse multiple entries into a segmented formula.
// When entries share the same width, height, qty and repeat count (exactly what
// rapid mode produces), factor out the common dimensions and sum the lengths:
//   Before: ((3)*(2.4))+((1.5)*(2.4))+((5)*(2.4))
//   After:  ((2.4)*(3+1.5+5))
// Produces cleaner, more auditable Excel formulas for QS review.
const SEGMENTED_SKIP = new Set(['Doors','Door frames','Windows','Windows (LM)','Windows (M2)','Stairs']);

function trySegmentedFormula(entries) {
  if (entries.length < 2) return null;

  // Every entry must have a length
  const lengths = entries.map(e => n(e.length));
  if (lengths.some(l => !l)) return null;

  // All entries must share the same width, height, qty, and repeat count
  const refW  = n(entries[0].width);
  const refH  = n(entries[0].height);
  const refQ  = n(entries[0].quantity, 1);
  const refRC = entries[0]._repeatCount || 1;

  const allMatch = entries.every(e =>
    n(e.width) === refW &&
    n(e.height) === refH &&
    n(e.quantity, 1) === refQ &&
    (e._repeatCount || 1) === refRC
  );
  if (!allMatch) return null;

  // Build: (sharedDims)*(L1+L2+...+Ln)*(qty)*(repeat)
  const segLengths = lengths.map(String).join('+');
  const shared = [refW, refH].filter(Boolean);

  // Shared dims first (constant), then segmented lengths (variable)
  const parts = shared.map(d => `(${d})`);
  parts.push(`(${segLengths})`);
  if (refQ !== 1) parts.push(`(${refQ})`);

  let formula = parts.join('*');
  if (refRC > 1) formula = `(${formula})*(${refRC})`;
  return formula;
}

function entryFormula(surfaceName, e) {
  const base = e.baseSurface || surfaceName;
  const qty = n(e.quantity, 1);
  const sides = e.sides === 'both' ? 2 : 1;
  switch (base) {
    case 'Doors': { const dw=n(e.doorWidth,0.9),dh=n(e.doorHeight,2.1); return (dw&&dh)?`(${qty})*(${dw})*(${dh})*(${sides})`:null; }
    case 'Door frames': { const dw=n(e.doorWidth,0.9),dh=n(e.doorHeight,2.1),fw=n(e.frameWidth,0.05); return (dw&&dh)?`(${qty})*(2)*((${dw})+(${dh}))*(${fw})*(${sides})`:null; }
    case 'Windows': { const fw=n(e.frameWidth),fl=n(e.frameLength); return (fw&&fl)?`(${qty})*(${fw})*(${fl})`:null; }
    case 'Windows (LM)': { const fw=n(e.frameWidth),fl=n(e.frameLength); return (fw&&fl)?`(${qty})*((2)*(${fw})+(2)*(${fl}))`:null; }
    case 'Windows (M2)': { const ww=n(e.winWidth||e.frameWidth),wh=n(e.winHeight||e.frameLength); if(!ww||!wh) return null; const qty=n(e.quantity,1); if(e.includeFrame){const fw=n(e.windowFrameWidth,0.05);return `(${qty})*((${ww})*(${wh})+(2)*((${ww})+(${wh}))*(${fw}))`;} return `(${qty})*(${ww})*(${wh})`; }
    case 'Stairs': {
      const l=n(e.length),w=n(e.width),h=n(e.height),q=n(e.quantity,1);
      const dims=[l,w,h].filter(Boolean); if(!dims.length) return null;
      let f=[...dims.map(d=>`(${d})`), ...(q!==1?[`(${q})`]:[])].join('*');
      if(e.includeLanding&&n(e.landingLength)&&n(e.landingWidth)) f+=`+(${n(e.landingLength)})*(${n(e.landingWidth)})`;
      return f;
    }
    case 'Skirting': case 'Fascia': case 'Gutter': case 'Downpipes': case 'Slab Edge': {
      const l=n(e.length); if(!l) return null;
      return qty!==1 ? `(${l})*(${qty})` : `(${l})`;
    }
    case 'Posts': {
      const h=n(e.height); if(!h) return null;
      return qty!==1 ? `(${h})*(${qty})` : `(${h})`;
    }
    default: {
      const l=n(e.length),w=n(e.width),h=n(e.height);
      const dims=[l,w,h].filter(Boolean); if(!dims.length) return null;
      const parts=dims.map(d=>`(${d})`);
      if(qty!==1) parts.push(`(${qty})`);
      return parts.join('*');
    }
  }
}

function buildFormula(surfaceName, entries) {
  if (!entries?.length) return null;

  // Resolve base surface for skip check (e.g. "Doors 2" → "Doors")
  const base = entries[0]?.baseSurface || surfaceName;

  // Segmented formula for standard surfaces — collapses rapid-mode runs
  if (!SEGMENTED_SKIP.has(base) && entries.length >= 2) {
    const seg = trySegmentedFormula(entries);
    if (seg) return `(${seg})`;
  }

  // Fallback — individual entry formulas joined with +
  const parts = entries.map(e => {
    const f = entryFormula(surfaceName, e);
    if (!f) return null;
    const rc = e._repeatCount || 1;
    return rc > 1 ? `(${f})*(${rc})` : f;
  }).filter(Boolean);
  if (!parts.length) return null;
  return parts.length === 1 ? `(${parts[0]})` : parts.map(p => `(${p})`).join('+');
}

function groupExtBySubstrate(surfaceEntries) {
  const map = {}; const order = [];
  (surfaceEntries || []).forEach(entry => {
    if (!entry) return;
    const sub = (entry.substrate || '').trim();
    if (sub === 'Windows') {
      if ((entry.windowMeasureType || 'area') === 'frame') {
        const k='Windows (LM)'; if(!map[k]){map[k]=[];order.push(k);} map[k].push(entry);
      } else {
        const k='Windows (M2)'; if(!map[k]){map[k]=[];order.push(k);} map[k].push({...entry, winWidth:entry.frameWidth, winHeight:entry.frameLength});
      }
      return;
    }
    if(!map[sub]){map[sub]=[];order.push(sub);} map[sub].push(entry);
  });
  return { map, order };
}

// ─── Surface settings ──────────────────────────────────────────────────────

function getSet(area, surfKey, key) {
  return area.surfaceSettings?.[surfKey]?.[key] ?? null;
}

// Default production rates and girths per surface (from template)
const DEFAULT_RATES = {
  'Ceilings': 18, 'Ceilings Detailed': 15, 'Walls': 18, 'Walls Detailed': 15,
  'Windows': 15, 'Windows (LM)': 15, 'Windows (M2)': 3,
  'Doors': 6, 'Door frames': 15, 'Skirting': 18, 'Stairs': 12,
  'Gutter': 18, 'Fascia': 18, 'Eaves': 15, 'Soffit': 15,
  'Walls - Flat': 18, 'Walls Rough': 10, 'Downpipes': 16,
  'Balustrades/Handrails': 6, 'Posts': 16,
};
// Resolve the base surface name for default lookups (e.g. "Walls 2" → "Walls")
function getBase(area, surfKey) {
  return area.surfaceSettings?.[surfKey]?.baseSurface || surfKey;
}

// Get the effective rate/girth: user setting > surface default > template Other default
function getEffectiveRate(area, surfKey) {
  return getSet(area, surfKey, 'paintingSpeed') ?? DEFAULT_RATES[getBase(area, surfKey)] ?? 20;
}
function getEffectiveGirth(area, surfKey) {
  return getSet(area, surfKey, 'girth') ?? DEFAULT_GIRTHS[getBase(area, surfKey)] ?? 1;
}
function getEffectivePrep(area, surfKey) {
  return getSet(area, surfKey, 'prepRate') ?? 75;
}
function getEffectiveCoats(area, surfKey) {
  const base = getBase(area, surfKey);
  const defaultCoats = base === 'Doors' || base === 'Door frames' ? 3 : 2;
  return getSet(area, surfKey, 'coats') ?? defaultCoats;
}

// True when THIS area rates this substrate as Good (green). Per-area, per-substrate.
// surfKey is the substrate key used throughout the export. conditionBySubstrate is keyed
// by the substrate captured in the condition UI; for independent blocks ("Walls 2") we
// also check the base surface ("Walls").
function isSoundSurface(area, surfKey) {
  const cond = area?.conditionBySubstrate || {};
  if (cond[surfKey]?.condition === 'good') return true;
  const base = area?.surfaceSettings?.[surfKey]?.baseSurface;
  if (base && cond[base]?.condition === 'good') return true;
  // External windows are exported under measure-mode keys ('Windows (LM)'/'Windows (M2)')
  // but condition is captured against the base substrate 'Windows'.
  if (surfKey.startsWith('Windows') && cond['Windows']?.condition === 'good') return true;
  return false;
}

// Write all surface settings to a row: girth(B=2), coats(D=4), speed(E=5), prep(M=13)
// In exclude-sound mode, Good surfaces get 0 coats AND 0 prep — both feed labour hours
// via H = C*D/E + IF(M>0, C/M, 0), so zeroing both drives hours/cost/paint to 0 while
// Col C (m²) stays intact.
function writeSurfSettings(xml, area, surfKey, row) {
  const sound = _excludeSoundMode && isSoundSurface(area, surfKey);
  xml = patchNumber(xml, toAddr(row, 2), getEffectiveGirth(area, surfKey));
  xml = patchNumber(xml, toAddr(row, 4), sound ? 0 : getEffectiveCoats(area, surfKey)); // coats
  xml = patchNumber(xml, toAddr(row, 5), getEffectiveRate(area, surfKey));
  xml = patchNumber(xml, toAddr(row, 13), sound ? 0 : getEffectivePrep(area, surfKey)); // prep
  return xml;
}

// Wrap a formula with method prep multiplier for spray setup time.
// Abseil is excluded — its rate is captured at area level via abseilPct
// and exported to the dedicated Excel abseil % cell, so m² stays clean.
function applyMethodPrep(formula, area, surfKey) {
  const settings = area.surfaceSettings?.[surfKey] || {};
  const method = settings.method || 'brush_roll';
  if (method === 'brush_roll' || method === 'abseil') return formula;
  const prep = settings.methodPrep ?? 0;
  if (!prep || prep <= 0) return formula;
  const multiplier = Math.round((1 + prep) * 100) / 100;
  return `(${formula})*(${multiplier})`;
}

// Returns " (method=spray)" or " (method=abseil)" for non-default methods, or "" for brush/roll
function methodTag(area, surfKey) {
  const method = area.surfaceSettings?.[surfKey]?.method || 'brush_roll';
  if (method === 'brush_roll') return '';
  return ` (method=${method})`;
}

// Returns " (condition=good — not repainted)" when exclude-sound mode zeroed this
// surface, else "". Mirrors the methodTag pattern so the estimator sees why a row is zero.
function soundTag(area, surfKey) {
  return (_excludeSoundMode && isSoundSurface(area, surfKey)) ? ' (condition=good — not repainted)' : '';
}

// ─── Write surfaces to area block ─────────────────────────────────────────

function writeIntArea(xml, area, idx, adjustedHeaders, insertedSlots) {
  const hdr = adjustedHeaders ? adjustedHeaders[idx] : INT_HEADERS[idx];
  const extraSlots = insertedSlots ? (insertedSlots[`int_${idx}`] || []) : [];
  const label = area.tag ? `${area.name} — ${area.tag}` : (area.name || `Area ${idx+1}`);
  xml = patchString(xml, toAddr(hdr, 1), label);

  const groups = area.repeatGroups || [];
  const groupMap = {};
  groups.forEach(g => { groupMap[g.id] = g; });

  // Separate ungrouped and grouped entries
  const ungrouped = (area.surfaceEntries || []).filter(e => !e.repeatGroupId);
  const grouped = (area.surfaceEntries || []).filter(e => e.repeatGroupId);

  // Group ungrouped by substrate
  const bySubstrate = {};
  ungrouped.forEach(e => {
    const k = e.substrate || '';
    if (!bySubstrate[k]) bySubstrate[k] = [];
    bySubstrate[k].push(e);
  });

  const usedOffsets = new Set();

  // Windows — both measure modes merged into single row (+5)
  const allWin = bySubstrate['Windows'] || [];
  if (allWin.length) {
    const areaWin = allWin.filter(e => (e.windowMeasureType||'area')!=='frame');
    const frameWin = allWin.filter(e => (e.windowMeasureType||'area')==='frame');
    const parts = [];
    if (areaWin.length) { const f=buildFormula('Windows (M2)',areaWin.map(e=>({...e,winWidth:e.frameWidth,winHeight:e.frameLength}))); if(f) parts.push(f); }
    if (frameWin.length) { const f=buildFormula('Windows (LM)',frameWin); if(f) parts.push(f); }
    if (parts.length) {
      usedOffsets.add(5);
      let combined = parts.length===1 ? parts[0] : parts.map(p=>`(${p})`).join('+');
      combined = applyMethodPrep(combined, area, 'Windows');
      xml = patchFormula(xml, toAddr(hdr+5, 3), combined);
      const winTag = methodTag(area, 'Windows') + soundTag(area, 'Windows');
      if (winTag) xml = patchString(xml, toAddr(hdr+5, 1), `Windows${winTag}`);
      xml = writeSurfSettings(xml, area, 'Windows', hdr+5);
    }
  }

  // Named surfaces (except Windows)
  const named = {'Ceilings':2,'Walls':3,'Floors':4,'Doors':6,'Skirting':7};
  Object.entries(named).forEach(([surf, offset]) => {
    const entries = bySubstrate[surf] || [];
    if (!entries.length) return;
    let f = buildFormula(surf, entries);
    if (!f) return;
    f = applyMethodPrep(f, area, surf);
    usedOffsets.add(offset);
    const tag = methodTag(area, surf) + soundTag(area, surf);
    if (tag) xml = patchString(xml, toAddr(hdr+offset, 1), `${surf}${tag}`);
    xml = patchFormula(xml, toAddr(hdr+offset, 3), f);
    xml = writeSurfSettings(xml, area, surf, hdr+offset);
  });

  // Door frames — from ungrouped Doors with includeFrame
  const doorFrameEntries = (bySubstrate['Doors'] || []).filter(e => e.includeFrame);
  if (doorFrameEntries.length) {
    let f = buildFormula('Door frames', doorFrameEntries);
    if (f) {
      f = applyMethodPrep(f, area, 'Doors'); usedOffsets.add(9);
      const dfTag = methodTag(area, 'Doors') + soundTag(area, 'Doors');
      if (dfTag) xml = patchString(xml, toAddr(hdr+9, 1), `Door frames${dfTag}`);
      xml = patchFormula(xml, toAddr(hdr+9, 3), f); xml = writeSurfSettings(xml, area, 'Doors', hdr+9);
    }
  }

  // Non-named ungrouped surfaces → overflow
  const namedSet = new Set([...Object.keys(named), 'Windows']);
  const extras = [];
  Object.entries(bySubstrate).forEach(([surf, entries]) => {
    if (namedSet.has(surf) || !surf) return;
    let f = buildFormula(surf, entries);
    if (f) { f = applyMethodPrep(f, area, surf); extras.push({label: surf + methodTag(area, surf) + soundTag(area, surf), formula: f, surfKey: surf}); }
  });

  // Repeat group entries → separate named rows
  groups.forEach(g => {
    const groupEntries = grouped.filter(e => e.repeatGroupId === g.id);
    const grpBySubstrate = {};
    groupEntries.forEach(e => {
      const k = e.substrate || ''; if (!k) return;
      if (!grpBySubstrate[k]) grpBySubstrate[k] = [];
      grpBySubstrate[k].push({ ...e, _repeatCount: g.count || 1 });
    });
    Object.entries(grpBySubstrate).forEach(([sub, entries]) => {
      let f = buildFormula(sub, entries); if (!f) return;
      f = applyMethodPrep(f, area, sub);
      const groupLabel = `${g.name||'Group'}-${sub}-x${g.count||1}${methodTag(area, sub)}${soundTag(area, sub)}`;
      extras.push({ label: groupLabel, formula: f, surfKey: sub });
    });
  });

  const avail = [...INT_OTHER_OFFSETS, ...INT_NAMED_OFFSETS.filter(o=>!usedOffsets.has(o)), INT_GAP_OFFSET].filter(o=>!usedOffsets.has(o));
  extras.forEach((item, i) => {
    let row;
    if (i < avail.length) {
      const offset = avail[i]; usedOffsets.add(offset);
      row = hdr + offset;
    } else {
      const slotIdx = i - avail.length;
      if (slotIdx >= extraSlots.length) { console.warn('ExcelExport: overflow — max surfaces per area reached'); return; }
      row = extraSlots[slotIdx];
    }
    xml = patchString(xml, toAddr(row, 1), item.label);
    xml = patchFormula(xml, toAddr(row, 3), item.formula);
    xml = writeSurfSettings(xml, area, item.surfKey, row);
  });

  // Clear column C (measurement) for any surface row not used, so residual
  // template example data never leaks into the quote.
  [...INT_NAMED_OFFSETS, ...INT_OTHER_OFFSETS, INT_GAP_OFFSET].forEach(o => {
    if (!usedOffsets.has(o)) xml = clearFormulaCell(xml, toAddr(hdr + o, 3));
  });

  // Shift for offsets after the gap row (inserted rows push structural rows down)
  const intShift = extraSlots.length;

  // Downtime → col E of downtime row
  const dt = (area.downtime ?? 10) / 100;
  xml = patchNumber(xml, toAddr(hdr + INT_DOWNTIME_OFFSET + intShift, 5), dt);

  // Abseil % → col H of abseil row
  xml = patchNumber(xml, toAddr(hdr + INT_ABSEIL_OFFSET + intShift, 8), (area.abseilPct ?? 0) / 100);

  // Preliminary items → Col A of prelim rows
  const prelims = area.preliminaryItems || [];
  prelims.forEach((item, i) => {
    if (i >= INT_PRELIM_OFFSETS.length) return;
    xml = patchString(xml, toAddr(hdr + INT_PRELIM_OFFSETS[i] + intShift, 1), item);
  });

  // Scope notes
  if (area.scopeNotes) {
    const nOff = idx===0 ? INT_NOTE_OFFSET_A1 : INT_NOTE_OFFSET;
    try { xml = patchString(xml, toAddr(hdr+nOff+intShift, 2), area.scopeNotes); } catch {}
  }

  // Scope exclusions
  if (area.scopeExclusions && area.scopeExclusions.trim()) {
    if (idx===0) {
      const lines = area.scopeExclusions.trim().split('\n').filter(l=>l.trim());
      INT_EXCL_OFFSETS_A1.forEach((off,i) => { if(i<lines.length) xml=patchString(xml, toAddr(hdr+off+intShift, 2), lines[i].trim()); });
    } else {
      xml = patchString(xml, toAddr(hdr+INT_EXCL_OFFSET+intShift, 2), area.scopeExclusions.trim());
    }
  }

  // Access equipment flag — bold red text in notes cell
  const intAccessNeeds = area.accessNeeds || [];
  const intHasAccessEquip = intAccessNeeds.some(id => ACCESS_EQUIPMENT_IDS.has(id));
  if (intHasAccessEquip) {
    const equipNames = [];
    if (intAccessNeeds.some(id => id.startsWith('scissor_lift'))) equipNames.push('SCISSOR LIFT');
    if (intAccessNeeds.some(id => id.startsWith('boom_lift'))) equipNames.push('BOOM LIFT');
    const flagText = `ACCESS EQUIPMENT REQUIRED: ${equipNames.join(', ')}`;
    const intNOff = idx===0 ? INT_NOTE_OFFSET_A1 : INT_NOTE_OFFSET;
    const intNoteAddr = toAddr(hdr + intNOff + intShift, 2);
    if (area.scopeNotes) {
      const combined = `<r><rPr><b/><sz val="10"/><color rgb="FFFF0000"/><rFont val="Arial"/></rPr><t>${escXml(flagText + '\n')}</t></r><r><rPr><sz val="10"/><rFont val="Arial"/></rPr><t>${escXml(area.scopeNotes)}</t></r>`;
      const re = cellRe(intNoteAddr);
      xml = xml.replace(re, (_, attrs) => {
        const s = styleAttr(attrs);
        return `<c r="${intNoteAddr}"${s} t="inlineStr"><is>${combined}</is></c>`;
      });
    } else {
      xml = patchRedString(xml, intNoteAddr, flagText);
    }
  }

  return xml;
}

function writeExtArea(xml, area, idx, adjustedHeaders, insertedSlots) {
  const hdr = adjustedHeaders ? adjustedHeaders[idx] : EXT_HEADERS[idx];
  const extraSlots = insertedSlots ? (insertedSlots[`ext_${idx}`] || []) : [];
  const label = area.tag ? `${area.name} — ${area.tag}` : (area.name || `Ext Area ${idx+1}`);
  xml = patchString(xml, toAddr(hdr, 1), label);

  const groups = area.repeatGroups || [];
  const groupMap = {};
  groups.forEach(g => { groupMap[g.id] = g; });

  // Separate ungrouped and grouped entries
  const ungrouped = (area.surfaceEntries || []).filter(e => !e.repeatGroupId);
  const grouped = (area.surfaceEntries || []).filter(e => e.repeatGroupId);

  const usedOffsets = new Set();
  const overflow = [];

  // 1. Write ungrouped entries to named slots
  const { map: extMap, order: extOrder } = groupExtBySubstrate(ungrouped);
  extOrder.forEach(sub => {
    const entries = extMap[sub];
    let f = buildFormula(sub, entries); if (!f) return;
    // Map sub back to original surface key for method lookup
    const origSurf = sub.startsWith('Windows') ? 'Windows' : sub;
    f = applyMethodPrep(f, area, origSurf);
    const tag = methodTag(area, origSurf) + soundTag(area, sub);
    const offsetKey = Object.keys(EXT_SURFACE_OFFSETS).find(k=>k.trim()===sub);
    const offset = offsetKey!==undefined ? EXT_SURFACE_OFFSETS[offsetKey] : undefined;
    if (offset!==undefined && !usedOffsets.has(offset)) {
      usedOffsets.add(offset);
      if (tag) xml = patchString(xml, toAddr(hdr+offset, 1), `${sub}${tag}`);
      xml = patchFormula(xml, toAddr(hdr+offset, 3), f);
      xml = writeSurfSettings(xml, area, sub, hdr+offset);
    } else {
      overflow.push({label: sub + tag, formula:f, surfKey:sub});
    }
  });

  // 2. Write repeat group entries as independent named rows
  groups.forEach(g => {
    const groupEntries = grouped.filter(e => e.repeatGroupId === g.id);
    const bySubstrate = {};
    groupEntries.forEach(e => {
      const k = e.substrate || ''; if (!k) return;
      if (!bySubstrate[k]) bySubstrate[k] = [];
      bySubstrate[k].push({ ...e, _repeatCount: g.count || 1 });
    });
    Object.entries(bySubstrate).forEach(([sub, entries]) => {
      let f = buildFormula(sub, entries); if (!f) return;
      f = applyMethodPrep(f, area, sub);
      const groupLabel = `${g.name||'Group'}-${sub}-x${g.count||1}${methodTag(area, sub)}${soundTag(area, sub)}`;
      overflow.push({ label: groupLabel, formula: f, surfKey: sub });
    });
  });

  // 3. Place overflow into available slots (existing + dynamically inserted)
  const avail = [...EXT_OTHER_OFFSETS, ...EXT_NAMED_OFFSETS.filter(o=>!usedOffsets.has(o)), EXT_GAP_OFFSET].filter(o=>!usedOffsets.has(o));
  overflow.forEach((item, i) => {
    let row;
    if (i < avail.length) {
      const offset = avail[i]; usedOffsets.add(offset);
      row = hdr + offset;
    } else {
      const slotIdx = i - avail.length;
      if (slotIdx >= extraSlots.length) { console.warn('ExcelExport: ext overflow'); return; }
      row = extraSlots[slotIdx];
    }
    xml = patchString(xml, toAddr(row, 1), item.label);
    xml = patchFormula(xml, toAddr(row, 3), item.formula);
    xml = writeSurfSettings(xml, area, item.surfKey, row);
  });

  // Clear column C (measurement) for any surface row not used, so residual
  // template example data never leaks into the quote.
  [...EXT_NAMED_OFFSETS, ...EXT_OTHER_OFFSETS, EXT_GAP_OFFSET].forEach(o => {
    if (!usedOffsets.has(o)) xml = clearFormulaCell(xml, toAddr(hdr + o, 3));
  });

  // Shift for offsets after the gap row (inserted rows push structural rows down)
  const extShift = extraSlots.length;

  // Downtime → col E of downtime row
  const dt = (area.downtime ?? 10) / 100;
  xml = patchNumber(xml, toAddr(hdr + EXT_DOWNTIME_OFFSET + extShift, 5), dt);

  // Abseil % → col H of abseil row
  xml = patchNumber(xml, toAddr(hdr + EXT_ABSEIL_OFFSET + extShift, 8), (area.abseilPct ?? 0) / 100);

  // Preliminary items → Col A of prelim rows
  const prelims = area.preliminaryItems || [];
  prelims.forEach((item, i) => {
    if (i >= EXT_PRELIM_OFFSETS.length) return;
    xml = patchString(xml, toAddr(hdr + EXT_PRELIM_OFFSETS[i] + extShift, 1), item);
  });

  // Wash: reference the total m² cell (shifted by inserted rows)
  const totalOff = idx===0 ? EXT_TOTAL_A1 : EXT_TOTAL;
  const totalRow = hdr + totalOff + extShift;
  xml = patchFormula(xml, toAddr(hdr + EXT_WASH_OFFSET + extShift, 3), `C${totalRow}`);

  // Scope notes
  if (area.scopeNotes) {
    const nOff = idx===0 ? EXT_NOTE_OFFSET_A1 : EXT_NOTE_OFFSET;
    try { xml = patchString(xml, toAddr(hdr+nOff+extShift, 2), area.scopeNotes); } catch {}
  }

  // Scope exclusions
  if (area.scopeExclusions && area.scopeExclusions.trim()) {
    if (idx===0) {
      const lines = area.scopeExclusions.trim().split('\n').filter(l=>l.trim());
      EXT_EXCL_OFFSETS_A1.forEach((off,i) => { if(i<lines.length) xml=patchString(xml, toAddr(hdr+off+extShift, 2), lines[i].trim()); });
    } else {
      xml = patchString(xml, toAddr(hdr+EXT_EXCL_OFFSET+extShift, 2), area.scopeExclusions.trim());
    }
  }

  // Access equipment flag — bold red text in notes cell
  const extAccessNeeds = area.accessNeeds || [];
  const extHasAccessEquip = extAccessNeeds.some(id => ACCESS_EQUIPMENT_IDS.has(id));
  if (extHasAccessEquip) {
    const equipNames = [];
    if (extAccessNeeds.some(id => id.startsWith('scissor_lift'))) equipNames.push('SCISSOR LIFT');
    if (extAccessNeeds.some(id => id.startsWith('boom_lift'))) equipNames.push('BOOM LIFT');
    const flagText = `ACCESS EQUIPMENT REQUIRED: ${equipNames.join(', ')}`;
    const extNOff = idx===0 ? EXT_NOTE_OFFSET_A1 : EXT_NOTE_OFFSET;
    const extNoteAddr = toAddr(hdr + extNOff + extShift, 2);
    if (area.scopeNotes) {
      const combined = `<r><rPr><b/><sz val="10"/><color rgb="FFFF0000"/><rFont val="Arial"/></rPr><t>${escXml(flagText + '\n')}</t></r><r><rPr><sz val="10"/><rFont val="Arial"/></rPr><t>${escXml(area.scopeNotes)}</t></r>`;
      const re = cellRe(extNoteAddr);
      xml = xml.replace(re, (_, attrs) => {
        const s = styleAttr(attrs);
        return `<c r="${extNoteAddr}"${s} t="inlineStr"><is>${combined}</is></c>`;
      });
    } else {
      xml = patchRedString(xml, extNoteAddr, flagText);
    }
  }

  return xml;
}

// ─── Collapse outline groups for unused area blocks ───────────────────────────
// Template has outlineLevel="1" groups for areas 2-20 (int) and 1-20 (ext).
// This hides rows for areas with no data and leaves filled areas expanded.

const INT_BLOCKS = [
  [79, 82, 83, -1],       // sub-group within int area 1 — always collapse
  [106, 159, 160, 1], [161, 214, 215, 2], [216, 269, 270, 3], [271, 324, 325, 4],
  [326, 379, 380, 5], [381, 434, 435, 6], [436, 489, 490, 7], [491, 544, 545, 8],
  [546, 599, 600, 9], [601, 654, 655, 10], [656, 709, 710, 11], [711, 764, 765, 12],
  [766, 819, 820, 13], [821, 874, 875, 14], [876, 929, 930, 15], [931, 984, 985, 16],
  [986, 1039, 1040, 17], [1041, 1094, 1095, 18], [1096, 1149, 1150, 19],
];

const EXT_BLOCKS = [
  [1190, 1193, 1194, -1],  // sub-group within ext area 1 — always collapse
  [1217, 1271, 1272, 1], [1273, 1327, 1328, 2], [1329, 1383, 1384, 3], [1385, 1439, 1440, 4],
  [1441, 1495, 1496, 5], [1497, 1551, 1552, 6], [1553, 1607, 1608, 7], [1609, 1663, 1664, 8],
  [1665, 1719, 1720, 9], [1721, 1775, 1776, 10], [1777, 1831, 1832, 11], [1833, 1887, 1888, 12],
  [1889, 1943, 1944, 13], [1945, 1999, 2000, 14], [2001, 2055, 2056, 15], [2057, 2111, 2112, 16],
  [2113, 2167, 2168, 17], [2169, 2223, 2224, 18], [2225, 2279, 2280, 19],
];

function collapseEmptyAreasAdj(xml, project, intBlocks, extBlocks) {
  const intCount = (project.areas||[]).filter(a => a.type === 'internal').length;
  const extCount = (project.areas||[]).filter(a => a.type === 'external').length;

  const hideRows = new Set();
  const summaryRows = new Set();
  const showRows = new Set();

  for (const [start, end, summary, idx] of intBlocks) {
    if (idx === -1 || idx >= intCount) {
      for (let r = start; r <= end; r++) hideRows.add(r);
      if (summary) summaryRows.add(summary);
    } else {
      for (let r = start; r <= end; r++) showRows.add(r);
    }
  }
  for (const [start, end, summary, idx] of extBlocks) {
    if (idx === -1 || idx >= extCount) {
      for (let r = start; r <= end; r++) hideRows.add(r);
      if (summary) summaryRows.add(summary);
    } else {
      for (let r = start; r <= end; r++) showRows.add(r);
    }
  }

  return xml.replace(/<row r="(\d+)"([^>]*)>/g, (match, rn, attrs) => {
    const rowNum = parseInt(rn);
    if (showRows.has(rowNum)) {
      return `<row r="${rn}"${attrs.replace(/\s*hidden="1"/, '')}>`;
    }
    if (hideRows.has(rowNum) && !attrs.includes('hidden="1"')) {
      return `<row r="${rn}"${attrs} hidden="1">`;
    }
    if (summaryRows.has(rowNum) && !attrs.includes('collapsed="1"')) {
      return `<row r="${rn}"${attrs} collapsed="1">`;
    }
    return match;
  });
}

// ─── Main export ──────────────────────────────────────────────────────────────

export async function exportProjectToExcel(project, options = {}) {
  // Mode 2: when excludeSound is true, surfaces rated Good (green) are exported with
  // 0 coats and 0 prep so the template formulas zero their hours/cost/paint, while the
  // measurement (Col C) stays visible for the estimator to re-add.
  const excludeSound = options.excludeSound === true;
  _excludeSoundMode = excludeSound; // module-scoped for writeSurfSettings/soundTag (set per run)

  // Template is IP — served only via the authenticated /api/template route, never as a
  // public static file. Caller passes the Firebase ID token; unauthenticated requests 401.
  const resp = await fetch('/api/template?name=excel', {
    headers: options.idToken ? { Authorization: `Bearer ${options.idToken}` } : {},
  });
  if (!resp.ok) throw new Error('Could not load the Excel template — please sign in and try again.');
  const arrayBuffer = await resp.arrayBuffer();
  const zip = await JSZip.loadAsync(arrayBuffer);
  let xml = await zip.file('xl/worksheets/sheet1.xml').async('text');

  // Load Summary sheet early — needed for row insertion cross-reference updates
  const summaryFile = zip.file('xl/worksheets/sheet2.xml');
  let summaryXml = summaryFile ? await summaryFile.async('text') : '';

  // ── Phase 1-5: Dynamic row insertion (BEFORE stripSharedFormulas) ──────────
  // Pre-scan areas for overflow, insert extra rows, shift formulas/refs globally
  const {
    xml: expandedXml, summaryXml: expandedSummary,
    adjustedIntHeaders, adjustedExtHeaders,
    adjustedIntBlocks, adjustedExtBlocks,
    insertedSlots,
  } = performRowInsertions(xml, summaryXml, project);
  xml = expandedXml;
  summaryXml = expandedSummary;

  // Strip shared formula mechanism AFTER row insertion — prevents Excel recovery warning
  xml = stripSharedFormulas(xml);

  // Metadata (rows ≤46 — unaffected by insertions)
  xml = patchString(xml, 'B3',  project.clientName||'');
  xml = patchString(xml, 'B4',  project.address||'');
  xml = patchString(xml, 'B13', project.projectName||project.clientName||'');
  xml = patchString(xml, 'B14', project.clientName||'');
  xml = patchString(xml, 'B15', project.address||'');
  xml = patchString(xml, 'B20', project.date||new Date().toISOString().slice(0,10));
  xml = patchString(xml, 'B21', project.estimatorName||'');
  xml = patchString(xml, 'B22', project.bdmName||'');
  xml = patchString(xml, 'B23', project.jobNumber||'');

  // Internal areas (with adjusted headers + inserted slots)
  (project.areas||[]).filter(a=>a.type==='internal').forEach((area, idx) => {
    if (idx >= adjustedIntHeaders.length) return;
    xml = writeIntArea(xml, area, idx, adjustedIntHeaders, insertedSlots);
  });

  // External areas (with adjusted headers + inserted slots)
  (project.areas||[]).filter(a=>a.type==='external').forEach((area, idx) => {
    if (idx >= adjustedExtHeaders.length) return;
    xml = writeExtArea(xml, area, idx, adjustedExtHeaders, insertedSlots);
  });

  // Collapse outline groups for unused area blocks (with adjusted boundaries)
  xml = collapseEmptyAreasAdj(xml, project, adjustedIntBlocks, adjustedExtBlocks);

  // ── Write unique preliminary item names to Summary sheet ────────────────
  const allAreas = project.areas || [];
  const uniquePrelims = [...new Set(allAreas.flatMap(a => a.preliminaryItems || []))];
  summaryXml = stripSharedFormulas(summaryXml);
  if (uniquePrelims.length > 0) {
    const maxSlots = SUMMARY_PRELIM_END - SUMMARY_PRELIM_START + 1; // 15
    uniquePrelims.slice(0, maxSlots).forEach((name, i) => {
      const row = SUMMARY_PRELIM_START + i;
      summaryXml = patchString(summaryXml, toAddr(row, 5), name); // Col E
    });
  }
  if (summaryFile) zip.file('xl/worksheets/sheet2.xml', summaryXml);

  // Force recalculation
  let wbXml = await zip.file('xl/workbook.xml').async('text');
  wbXml = wbXml.replace(/<calcPr([^/]*)\/?>/,(_, attrs) => {
    const cleaned = attrs.replace(/\s*fullCalcOnLoad="[^"]*"/,'');
    return `<calcPr${cleaned} fullCalcOnLoad="1"/>`;
  });
  zip.file('xl/workbook.xml', wbXml);
  zip.file('xl/worksheets/sheet1.xml', xml);

  // ── Remove stale calcChain — Excel regenerates it on open ────────────────
  // 1. Remove the file itself (try all known path variations)
  Object.keys(zip.files).forEach(path => {
    if (/calcChain/i.test(path)) zip.remove(path);
  });

  // 2. Remove Override from [Content_Types].xml
  const ctFile = zip.file('[Content_Types].xml');
  if (ctFile) {
    let ct = await ctFile.async('text');
    ct = ct.replace(/<Override[^>]*[Cc]alc[Cc]hain[^>]*\/?>/g, '');
    zip.file('[Content_Types].xml', ct);
  }

  // 3. Remove Relationship from workbook.xml.rels
  const relsFile = zip.file('xl/_rels/workbook.xml.rels');
  if (relsFile) {
    let rels = await relsFile.async('text');
    rels = rels.replace(/<Relationship[^>]*[Cc]alc[Cc]hain[^>]*\/?>/g, '');
    zip.file('xl/_rels/workbook.xml.rels', rels);
  }

  const content = await zip.generateAsync({ type:'arraybuffer', compression:'DEFLATE', compressionOptions:{level:6} });
  const blob = new Blob([content], { type:'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  const safePart = (s) => (s||'').replace(/[^a-zA-Z0-9\s]/g,'').trim().replace(/\s+/g,'_');
  const suffix = excludeSound ? '_ExclSound' : '';
  const xlsxName = [safePart(project.jobNumber),safePart(project.clientName||project.projectName),project.date||new Date().toISOString().slice(0,10)].filter(Boolean).join('_');
  a.href = url; a.download = `${xlsxName}${suffix}.xlsx`;
  document.body.appendChild(a); a.click(); document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// ─── Test exports (formula builders used by calcUtils.test.js) ──────────────
export { entryFormula as _entryFormula, buildFormula as _buildFormula, trySegmentedFormula as _trySegmentedFormula };
